import React from 'react';
import { SEOHeader } from '@/components/Headers';
import { ShieldCheckIcon } from '@heroicons/react/outline';
import {
  // Import all components from the common index
  ActionButtonsCard,
  BookCover,
  BookDetails,
  BookStatusCard,
  CategoryChips,
  ChecklistCard,
  ContentCard,
  DescriptionCard,
  MapLocationCard,
  OwnerCard,
  StatsCard
} from '@/components/common';
import { Button } from '@heroui/react';
import { IoNavigateCircleOutline } from 'react-icons/io5';

// Function to generate Google Maps URL with walking directions
const getGoogleMapsDirectionsUrl = (coordinates) => {
  const [lat, lng] = coordinates;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
};

/**
 * BookCopyPage - Page component for displaying a book copy details
 */
export default function BookCopyPage({ bookcopy }) {

  // Prepare SEO data
  const seo = {
    title: `${bookcopy.isbn?.title || 'Egzemplarz książki'} | Lektuarium`,
    description: `Egzemplarz książki ${bookcopy.isbn?.title} dostępny w bibliotece ${bookcopy.user?.nickname}`,
    img: bookcopy.isbn?.thumbnailMedium || require('@/assets/images/book_cover_unavailable.webp').default.src,
    slug: `/bookle/${bookcopy.uid}`,
    ogTitle: `${bookcopy.isbn?.title} | Lektuarium`,
  };

  // Define availability status
  const isAvailable = bookcopy.is_available;

  // Tips for borrowing
  const borrowingTips = [
    "Skontaktuj się z właścicielem książki poprzez stary dobry e-mail.",
    "Umów się na miejsce i czas odbioru książki.",
    "Zapytaj o możliwą kaucję, jeśli książka jest wartościowa.",
    "Zwróć książkę w ustalonym terminie i w dobrym stanie.",
  ];

  // Stats for the StatsCard component
  const bookStats = [
    {
      value: bookcopy.view_count || 0,
      label: "Zeskanowań kodu",
      bgColor: "bg-blue-50",
      valueColor: "text-blue-600",
      textColor: "text-blue-700"
    },
    {
      value: bookcopy.released_count || 0,
      label: "Wypożyczeń",
      bgColor: "bg-green-50",
      valueColor: "text-green-600",
      textColor: "text-green-700"
    }
  ];

  // Action buttons for the ActionButtonsCard
  const actionButtons = [
    {
      text: "Zobacz profil właściciela",
      href: `/u/${bookcopy.user?.nickname}`,
      color: "primary"
    },
    {
      text: "Przeczytaj więcej o katalogowaniu",
      href: "/docs",
      color: "primary",
      variant: "ghost"
    },
    {
      text: "Załóż własne Lektuarium",
      href: "/kody",
      color: "success",
      variant: "flat"
    }
  ];

  return (
    <>
      <SEOHeader {...seo} />

      <div className="relative py-8 sm:py-16 overflow-hidden">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 -z-10 h-full w-full bg-white [background:radial-gradient(125%_125%_at_50%_10%,#fff_40%,#e3f2fd_100%)]"></div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Book Title Header */}
          <div className="text-center mb-12">
            <h2 className="text-base font-semibold text-blue-600">Egzemplarz Książki</h2>
            <h1 className="my-2 font-baskervil text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
              {bookcopy.isbn?.title}
            </h1>
            {bookcopy.isbn?.subtitle && (
              <p className="mx-auto mt-3 max-w-2xl text-xl text-gray-500 font-cormorant py-2">
                {bookcopy.isbn.subtitle}
              </p>
            )}
            <p className="mx-auto mt-1 max-w-2xl text-xl font-cormorant text-gray-700 py-2">
              {bookcopy.isbn?.author}
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Left Column - Book Cover and Details */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Book Cover */}
                <div className="overflow-hidden rounded-xl shadow-xl bg-white">
                  <BookCover
                    title={bookcopy.isbn?.title}
                    thumbnailUrl={bookcopy.isbn?.thumbnailMedium}
                    width={500}
                    height={600}
                    className="aspect-[2/3] w-full"
                  />
                </div>

                {/* Book Status Card */}
                <BookStatusCard
                  isAvailable={isAvailable}
                  canBorrow={bookcopy.can_borrow}
                  ownerNickname={bookcopy.user?.nickname}
                />

                {/* Book Details Card */}
                <BookDetails
                  isbn={bookcopy.isbn}
                  createdAt={bookcopy.created_at}
                />
              </div>
            </div>

            {/* Middle Column - Owner and Borrowing Info */}
            <div className="lg:col-span-1">
              {/* Owner Card */}
              <OwnerCard
                user={bookcopy.user}
                className="mb-6"
              />

              {/* Borrowing Guidelines */}
              <ChecklistCard
                title="Jak wypożyczyć książkę"
                items={borrowingTips}
                className="mb-6"
                footerIcon={<ShieldCheckIcon className="h-7 w-7 text-blue-500" />}
                footerText="Każdy egzemplarz ma unikalny kod identyfikacyjny, który jest używany do wypożyczenia książki."
              />

              {/* Book Description */}
              <DescriptionCard
                title="Opis książki"
                description={bookcopy.isbn?.description}
                className="mb-6"
              />

              {/* Owner Notes */}
              <ContentCard
                title="Komentarz właściciela"
                content={bookcopy.content}
                className="mb-6"
                isItalic={true}
                isQuote={true}
              />
            </div>

            {/* Right Column - Map and Statistics */}
            <div className="lg:col-span-1">
              {/* Map Location */}
              <MapLocationCard
                coordinates={bookcopy.coordinates}
                className="mb-4"
              />

              <Button
                color="success"

                variant="solid"
                startContent={<IoNavigateCircleOutline className="h-5 w-5" />}
                onPress={() => window.open(getGoogleMapsDirectionsUrl(bookcopy.coordinates), '_blank')}
                className="shadow-md w-full mb-4 text-white"
              >
                Pójdź po egzemplarz
              </Button>


              {/* Statistics Card */}
              <StatsCard
                title="Statystyki egzemplarza"
                stats={bookStats}
                className="mb-6"
              />

              {/* Categories/Tags */}
              {bookcopy.isbn?.categories && (
                <CategoryChips
                  title="Kategorie"
                  categories={bookcopy.isbn.categories}
                  className="mb-6"
                />
              )}

              {/* Actions */}
              <ActionButtonsCard
                buttons={actionButtons}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

BookCopyPage.layout = 'Main';

export async function getStaticPaths() {
  let allBooks = [];
  let page = 1;
  let hasMorePages = true;

  while (hasMorePages) {
    const res = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/bookle/bookcopies?page=${page}`);
    const data = await res.json();

    allBooks = allBooks.concat(data.results);
    hasMorePages = data.next;  // Check if there are more pages to fetch
    page++;
  }

  // Map the slugs to paths
  const paths = allBooks.map((book) => ({
    params: { uid: book.uid },
  }));

  return {
    paths,
    fallback: false,
    // false: No new paths will be created, and missing pages will result in a 404.
    // true: New pages are created at runtime when requested (this can be useful, but pages may not be pre-built).
    // blocking: Ensures new pages are generated at build time if they aren't pre-rendered yet.
  };
}

export async function getStaticProps({ params }) {
  let uri = `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/bookle/bookcopies?uid=${params.uid}`;

  let response = await fetch(uri);
  let bookcopy = await response.json();
  bookcopy = bookcopy.results[0];

  if (!bookcopy) {
    return {
      notFound: true,  // Returns a 404 if the book is not found
    };
  }

  return {
    props: {
      bookcopy,
    },
    revalidate: 60, // In seconds
  };
}